import { useQuery } from '@tanstack/react-query';
import { Copy, EthernetPort, Link2, Loader, Share2 } from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useRestClient } from '../../api/RestClientContext';
import { Subscription, getSubTypeName } from '../../models/subscriptions';
import { TelegramProxyLink } from '../../models/tgProxy';
import Icon from '../../shared/components/Icon';
import LogoBar from '../../shared/components/LogoBar';
import CommonServerList from '../../shared/components/ServerList';
import { notify } from '../../shared/utils/notification';
import { useGetLink } from '../../shared/hooks/useGetLink';
import { copyToClipboard } from '../../utils/clipboard';
import { getTelegramWebApp, isTelegramMiniApp } from '../../utils/telegram';
import { PayHistoryScreen } from '../payhistory/PayHistoryScreen';
import './DashboardScreen.css';

export interface DashboardScreenProps {
    sub: Subscription
    openTelegramProxyOnMount?: boolean
    onTelegramProxyDialogHandled?: () => void
}

const iconSize: number = 24;

const TelegramProxyDialog: React.FC<{
    links: TelegramProxyLink[];
    isLoading: boolean;
    error: Error | null;
    onClose: () => void;
    onConnect: (link: TelegramProxyLink) => void;
}> = ({ links, isLoading, error, onClose, onConnect }) => {
    return (
        <div className="tg-proxy-dialog-overlay" onClick={onClose} role="button" tabIndex={0} onKeyDown={(e) => { if (e.key === 'Escape') onClose(); }} aria-label="Закрыть">
            <div className="tg-proxy-dialog glass-ui" style={{ boxShadow: 'none' }} onClick={(event) => event.stopPropagation()} role="dialog" aria-modal="true">
                <div className="tg-proxy-dialog-header">
                    <div>
                        <h3>Telegram proxy</h3>
                        <p>Выберите прокси и подключитесь в один тап.</p>
                        <p>⚠️ Телеграмм прокси может работать нестабильно с включенным VPN.</p>
                    </div>
                    <button
                        type="button"
                        className="tg-proxy-close-btn"
                        onClick={onClose}
                        aria-label="Закрыть"
                    >
                        ×
                    </button>
                </div>

                {isLoading && (
                    <div className="tg-proxy-dialog-state">Загружаем доступные прокси...</div>
                )}

                {!isLoading && error && (
                    <div className="tg-proxy-dialog-state error">
                        {error.message || 'Не удалось получить список прокси'}
                    </div>
                )}

                {!isLoading && !error && links.length === 0 && (
                    <div className="tg-proxy-dialog-state">
                        Сейчас нет доступных прокси.
                    </div>
                )}

                {!isLoading && !error && links.length > 0 && (
                    <div className="tg-proxy-list">
                        {links.map((link) => (
                            <div key={link.id} className="tg-proxy-item glass-ui">
                                <div className="tg-proxy-item-meta">
                                    <Link2 />
                                    <div className="tg-proxy-item-name">{link.friendlyName}IUL</div>
                                </div>
                                <div>
                                    <button
                                        type="button"
                                        className="tg-proxy-connect-btn"
                                        onClick={() => onConnect(link)}
                                    >
                                        Подключить
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export const DashboardScreen: React.FC<DashboardScreenProps> = React.memo(({
    sub,
    openTelegramProxyOnMount = false,
    onTelegramProxyDialogHandled,
}) => {
    const navigate = useNavigate();
    const restClient = useRestClient();
    const telegramRuntime = isTelegramMiniApp();
    const [isProxyDialogOpen, setIsProxyDialogOpen] = useState(false);
    const [shareLoading, setShareLoading] = useState(false);
    const [linkCopied, setLinkCopied] = useState(false);

    const { link, error, isLoading } = useGetLink();

    const {
        data: telegramProxyLinks = [],
        isLoading: telegramProxyLinksLoading,
        error: telegramProxyLinksError,
    } = useQuery({
        queryKey: ['telegram-proxy-links'],
        queryFn: () => restClient.getTelegramProxyLinks(),
        enabled: isProxyDialogOpen,
        staleTime: 10 * 60 * 1000,
        refetchOnWindowFocus: false,
    });

    const handlePaymentClick = useCallback(() => {
        navigate('/payment');
    }, [navigate]);

    const handleTermOfUseClick = useCallback(() => {
        navigate('/term_of_use');
    }, [navigate]);

    const handleTelegramProxyOpen = useCallback(() => {
        setIsProxyDialogOpen(true);
    }, []);

    const handleTelegramProxyClose = useCallback(() => {
        setIsProxyDialogOpen(false);
    }, []);

    const handleShare = useCallback(async () => {
        try {
            setShareLoading(true);
            const response = await restClient.getReferralHash();
            if (!response.result) {
                notify.error('Не удалось получить реферальную ссылку');
                return;
            }
            const referralLink = response.result;
            const tg = getTelegramWebApp();
            if (tg) {
                navigate('/loyalty');
                tg.openTelegramLink(`https://t.me/share/url?url=${encodeURIComponent(referralLink)}&text=${encodeURIComponent('Присоединяйся ко мне в OgoSecureAccess! Подписка активируется после того как нажмешь кнопку "НАЧАТЬ"')}`);
            } else {
                try {
                    await copyToClipboard(referralLink);
                    notify.success('Ссылка скопирована в буфер обмена');
                } catch {
                    notify.error('Ошибка при копировании ссылки');
                }
            }
        } catch {
            notify.error('Ошибка при попытке поделиться');
        } finally {
            setShareLoading(false);
        }
    }, [restClient, navigate]);

    const handleCopyLink = useCallback(async () => {
        if (error || isLoading || !link) return;
        const ok = await copyToClipboard(link.link);
        if (ok) {
            setLinkCopied(true);
            notify.info('Ссылка скопирована в буфер обмена');
            setTimeout(() => setLinkCopied(false), 2000);
        } else {
            notify.error('Не удалось скопировать ссылку');
        }
    }, [error, isLoading, link]);

    useEffect(() => {
        if (!openTelegramProxyOnMount) {
            return;
        }

        setIsProxyDialogOpen(true);
        onTelegramProxyDialogHandled?.();
    }, [onTelegramProxyDialogHandled, openTelegramProxyOnMount]);

    const handleTelegramProxyConnect = useCallback((proxyLink: TelegramProxyLink) => {
        try {
            const tg = getTelegramWebApp();

            let path = proxyLink.url;

            if (path.startsWith('tg://')) {
                if (path.startsWith('tg://proxy')) {
                    path = path.replace('tg://proxy', '/proxy');
                } else if (path.startsWith('tg://socks')) {
                    path = path.replace('tg://socks', '/socks');
                }

                if (tg?.postEvent) {
                    tg.postEvent('web_app_open_tg_link', {
                        path_full: path,
                    });

                    notify.success(`Открываем прокси: ${proxyLink.friendlyName}`);
                    return;
                }
            }

            const fallbackUrl = `https://t.me${path}`;
            window.open(fallbackUrl, '_blank', 'noopener,noreferrer');

            notify.success(`Открываем прокси: ${proxyLink.friendlyName}`);
        } catch (proxyError) {
            console.error(proxyError);
            notify.error('Не удалось открыть ссылку прокси');
        }
    }, []);

    const statsBlock = useMemo(() => (
        <div className="dashboard-stats-summary glass-ui stagger-item" style={{ '--animation-delay': '40ms' } as React.CSSProperties}>
            <div className="dashboard-stats-row">
                <Icon size={iconSize} name="gift" className="dashboard-info-icon" />
                <span className="dashboard-stats-label">тип подписки:</span>
                <span className="dashboard-stats-value">{getSubTypeName(sub.type)}</span>
            </div>
            <div className="dashboard-stats-row">
                <Icon size={iconSize} name="calendar" className="dashboard-info-icon" />
                <span className="dashboard-stats-label">осталось дней:</span>
                {sub.daysLeft > 0 ?
                    <span className="rainbow-pulse dashboard-stats-value" style={{ fontSize: '22px' }}>{sub.daysLeft}</span>
                    :
                    <span className='dashboard-stats-value pulse' style={{ color: 'var(--color-error)', fontSize: '25px' }}>0</span>
                }
            </div>
            <div className="dashboard-stats-row">
                <Icon size={iconSize} name="stats" className="dashboard-info-icon" />
                <span className="dashboard-stats-label">использовано:</span>
                <span className="dashboard-stats-value">{sub.usedGb} ГБ</span>
            </div>
        </div>
    ), [sub.type, sub.daysLeft, sub.usedGb]);

    const quickActionIconSize = 15;
    const quickActionsPanel = (
        <div className="dashboard-quick-actions glass-ui stagger-item" style={{ '--animation-delay': '80ms' } as React.CSSProperties}>
            <button
                type="button"
                className="quick-action-btn"
                onClick={handleShare}
                disabled={shareLoading}
                title="Поделиться приложением"
            >
                {shareLoading
                    ? <Loader size={quickActionIconSize} className="spin" />
                    : <Share2 size={quickActionIconSize} />}
                <span className="quick-action-label">поделиться</span>
            </button>

            <div className="quick-action-divider" />

            <button
                type="button"
                className="quick-action-btn"
                onClick={handleCopyLink}
                disabled={isLoading || !!error}
                title="Копировать ссылку"
            >
                <Copy size={quickActionIconSize} color={linkCopied ? '#4caf50' : undefined} />
                <span className="quick-action-label">ссылка</span>
            </button>

            <div className="quick-action-divider" />

            <button
                type="button"
                className="quick-action-btn"
                onClick={handleTelegramProxyOpen}
                title="Telegram proxy"
            >
                <EthernetPort size={quickActionIconSize} />
                <span className="quick-action-label">прокси</span>
            </button>
        </div>
    );

    return (
        <div className='dashboard-container'>
            <LogoBar />

            {telegramRuntime && (
                <button
                    type="button"
                    className={`img-button liquidGlass-wrapper menu pulse`}
                    style={{ animationDelay: '120ms', border: 'none' }}
                    onClick={handlePaymentClick}
                >
                    <div className="liquidGlass-effect"></div>
                    <div className="liquidGlass-tint"></div>
                    <div className="liquidGlass-shine"></div>
                    <div className={`try-button-text liquidGlass-text rainbow-pulse`}>
                        {sub.daysLeft <= 0 ? 'купить подписку' : 'продлить подписку'}
                    </div>
                </button>
            )}

            {statsBlock}

            {quickActionsPanel}

            <div className="stagger-item" style={{ '--animation-delay': '180ms' } as React.CSSProperties}>
                <CommonServerList isInternal={true} />
            </div>

            <PayHistoryScreen delay={240} optClass='stagger-item' />

            <div className="dashboard-footer stagger-item" style={{ '--animation-delay': '300ms' } as React.CSSProperties}>
                <button type="button" className='term-of-use-link' onClick={handleTermOfUseClick} style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', font: 'inherit', color: 'inherit' }}>
                    политика конфиденциальности
                </button>
            </div>

            {isProxyDialogOpen && (
                <TelegramProxyDialog
                    links={telegramProxyLinks}
                    isLoading={telegramProxyLinksLoading}
                    error={telegramProxyLinksError}
                    onClose={handleTelegramProxyClose}
                    onConnect={handleTelegramProxyConnect}
                />
            )}
        </div>
    );
});
